import { Accessor, createMemo } from "solid-js";

import { Channel, VoiceParticipant } from "stoat.js";

import { useClient } from "@revolt/client";

export function getEffectiveVoiceState(
  channel: Channel,
  participant: VoiceParticipant,
) {
  const member = channel.server?.getMember(participant.userId);
  const serverMuted = member ? !member.canPublish : false;
  const serverDeafened = member ? !member.canReceive : false;
  const selfMuted = !participant.isPublishing();
  const selfDeafened = !participant.isReceiving();

  return {
    selfMuted,
    selfDeafened,
    serverMuted,
    serverDeafened,
    muted: selfMuted || serverMuted,
    deafened: selfDeafened || serverDeafened,
  };
}

export function getUserVoiceStatusSummary(
  channel: Channel,
  participant: VoiceParticipant,
) {
  const states: string[] = [];
  const voiceState = getEffectiveVoiceState(channel, participant);

  if (voiceState.serverMuted) {
    states.push("Server muted");
  } else if (voiceState.selfMuted) {
    states.push("Muted");
  }

  if (voiceState.serverDeafened) {
    states.push("Server deafened");
  } else if (voiceState.selfDeafened) {
    states.push("Deafened");
  }

  if (participant.isCamera()) {
    states.push("Camera");
  }

  if (participant.isScreensharing()) {
    states.push("Screen sharing");
  }

  return states.length ? states.join(", ") : "Connected";
}

export function useUserVoiceState(userId: string | Accessor<string>) {
  const client = useClient();

  return createMemo<{
    channel: Channel;
    participant: VoiceParticipant;
  } | undefined>(() => {
    const id = typeof userId === "function" ? userId() : userId;
    const current = client();

    if (!current || !id) {
      return;
    }

    for (const channel of current.channels.values()) {
      const participant = channel.voiceParticipants.get(id);

      if (participant) {
        return {
          channel,
          participant,
        };
      }
    }
  });
}
